import { Dispatch, SetStateAction, useMemo } from "react";
import Dropdown from "../../../../shared/components/Dropdown";
import { ImportTemplateListResponse } from "../../../../shared/entities/ImportTemplateListResponse";
import { TranslationService } from "../../../../../services/TranslationService";
import {
  RequiredManager,
  ValidationMessage
} from "../../../../shared/RequieredManager";
import { TemplateField, sheetColumns } from "../ImportTemplateEditBase";
import { InvoiceFields } from "../../../../../services/InvoiceService";
import {
  ClientExportField,
  ClientFields
} from "../../../../../services/ClientService";

type Props = {
  model: Partial<ImportTemplateListResponse.Item>;
  setModel: Dispatch<SetStateAction<Partial<ImportTemplateListResponse.Item>>>;
  clientFields: TemplateField[];
  invoiceFields: TemplateField[];
  requiredFieldIds: string[];
  requiredManager: RequiredManager;
};

const FieldSelectColumnList = ({
  model,
  setModel,
  clientFields,
  invoiceFields,
  requiredFieldIds,
  requiredManager
}: Props) => {
  const { translate } = TranslationService;

  const updateColumn =
    (data: TemplateField, isInvoiceField: boolean) =>
    (value?: string | number) => {
      const name = data.key;
      const isAdditional = name.toString().includes("additional");
      let fieldValue = data.key;

      if (isAdditional) {
        fieldValue = name.toString().replace("additional-", "");
      } else if (parseInt(name) < 0) {
        fieldValue = name;
      } else {
        // Resolver primero en el enum de la sección: hay claves repetidas entre
        // enums (ej. "amount" es InvoiceFields.amount = -3 y ClientFields.amount = -32).
        const invoiceField = InvoiceFields[name as keyof typeof InvoiceFields];
        const clientField =
          ClientExportField[name as keyof typeof ClientExportField] ||
          ClientFields[name as keyof typeof ClientFields];
        fieldValue = (
          isInvoiceField
            ? invoiceField || clientField
            : clientField || invoiceField
        ).toString();
      }

      const column = {
        ix: value,
        name: fieldValue,
        pattern: "",
        entity: data.entity
      };

      setModel((x) => {
        const Columns =
          x.Columns?.filter((y) => {
            const isAdditional = y.name?.toString().includes("additional");
            const xName = isAdditional
              ? y.name?.toString().replace("additional-", "")
              : y.name;
            return xName !== fieldValue;
          }) ?? [];
        if (column && column.ix && column.ix !== "") {
          Columns.push(column as ImportTemplateListResponse.Column);
        }

        return { ...x, Columns, ColumnData: JSON.stringify(Columns) };
      });
    };

  const availableSheetColumns = useMemo(
    () => getAvailableSheetColumns(model?.Columns, sheetColumns),
    [model?.Columns]
  );

  function addOwnValue(
    availableSheetColumns:
      | import("../../../../shared/components/Dropdown").DropdownItems<string>
      | { Value: string; Id: string }[],
    value: string | undefined
  ):
    | import("../../../../shared/components/Dropdown").DropdownItems<string>
    | { Value: string; Id: string }[] {
    const sheets = availableSheetColumns as { value: string; text: string }[];
    if (value === undefined || sheets.find((x) => x.value === value)) {
      return sheets;
    }
    const valueNum = parseInt(value);
    if (Number.isNaN(valueNum)) {
      return sheets;
    }
    const insertIndex = sheets.findIndex((x) => parseInt(x.value) > valueNum);
    const sheetValue = sheetColumns.find((x) => x.value === valueNum)!;
    return [
      ...sheets.slice(0, insertIndex),
      { text: sheetValue.text, value: sheetValue.value.toString() },
      ...sheets.slice(insertIndex)
    ];
  }

  return (
    <>
      <div className="mb-3">
        <h6 className="mb-4">
          {translate.PapperData + " " + translate.Invoices}{" "}
        </h6>
      </div>
      {invoiceFields.map((x) => {
        const value =
          model?.Columns?.find(
            (y) =>
              y.name.toString()?.replace("additional-", "") === x.fieldId ||
              y.name.toString()?.replace("additional-", "") === x.key ||
              InvoiceFields[parseInt(y.name)] === x.fieldId ||
              InvoiceFields[parseInt(y.name)] === x.key
          )?.ix ?? undefined;

        return requiredFieldIds.includes(x.fieldId) ? (
          <HorizontalItem
            key={x.key}
            label={x.label}
            fontWeight={
              parseInt(x.fieldId) > 0 && x.required
                ? "fw-bold text-dark"
                : "fw-normal"
            }
          >
            <Dropdown
              onChange={requiredManager.makeRequiredWithId(
                updateColumn(x, true),
                x.fieldId
              )}
              items={addOwnValue(availableSheetColumns, value) as any}
              optionLabel={translate.Select}
              selectClasses={"w-sm"}
              defaultValue={value?.toString()}
            />
            <ValidationMessage
              onChange={requiredManager.getValidation(x.fieldId)!}
              defaultValue={value}
            />
          </HorizontalItem>
        ) : (
          <HorizontalItem
            key={x.key}
            label={x.label}
            fontWeight={
              parseInt(x.fieldId) > 0 && x.required
                ? "fw-bold text-dark"
                : "fw-normal"
            }
          >
            <Dropdown
              onChange={updateColumn(x, true)}
              items={addOwnValue(availableSheetColumns, value)}
              optionLabel={translate.Ignore}
              selectClasses={"w-sm"}
              defaultValue={value?.toString()}
            />
          </HorizontalItem>
        );
      })}

      <div className="mb-3">
        <h6 className="mb-4">
          {translate.PapperData + " " + translate.Clients}{" "}
        </h6>
      </div>
      {clientFields.map((x) => {
        const value =
          model?.Columns?.find(
            (y) =>
              y.name.toString()?.replace("additional-", "") === x.fieldId ||
              y.name.toString()?.replace("additional-", "") === x.key ||
              ClientFields[parseInt(y.name)] === x.fieldId ||
              ClientFields[parseInt(y.name)] === x.key ||
              ClientExportField[parseInt(y.name)] === x.fieldId ||
              ClientExportField[parseInt(y.name)] === x.key
          )?.ix ?? undefined;
        return requiredFieldIds.includes(x.fieldId) ? (
          <HorizontalItem
            key={x.key}
            label={x.label}
            fontWeight={
              parseInt(x.fieldId) > 0 && x.required
                ? "fw-bold text-dark"
                : "fw-normal"
            }
          >
            <Dropdown
              onChange={requiredManager.makeRequiredWithId(
                updateColumn(x, false),
                x.fieldId
              )}
              items={addOwnValue(availableSheetColumns, value) as any}
              optionLabel={translate.Select}
              selectClasses={"w-sm"}
              defaultValue={value?.toString()}
            />
            <ValidationMessage
              onChange={requiredManager.getValidation(x.fieldId)!}
              defaultValue={value}
            />
          </HorizontalItem>
        ) : (
          <HorizontalItem
            key={x.key}
            label={x.label}
            fontWeight={
              parseInt(x.fieldId) > 0 && x.required
                ? "fw-bold text-dark"
                : "fw-normal"
            }
          >
            <Dropdown
              onChange={updateColumn(x, false)}
              items={addOwnValue(availableSheetColumns, value)}
              optionLabel={translate.Ignore}
              selectClasses={"w-sm"}
              defaultValue={value?.toString()}
            />
          </HorizontalItem>
        );
      })}
    </>
  );
};

const HorizontalItem = ({
  label: Label,
  children,
  fontWeight = "fw-normal"
}: React.PropsWithChildren<{
  label: string | (() => JSX.Element);
  fontWeight?: string;
}>) => {
  return (
    <div className="row mb-3">
      <label className={"col-4 w-lg pt-0 col-form-label " + fontWeight}>
        {Label as string}
      </label>
      <div className="col-4">{children}</div>
    </div>
  );
};

function getAvailableSheetColumns(
  columns: Partial<ImportTemplateListResponse.Item>["Columns"] | undefined,
  sheetColumns: readonly { value: number; text: string }[]
): readonly { value: string; text: string }[] {
  if (columns === undefined) {
    return sheetColumns.map((x) => ({
      text: x.text,
      value: x.value.toString()
    }));
  }
  const usedColumns =
    columns
      ?.filter((x) => x.name !== "referenceNumber")
      .map((x) => parseInt(x.ix)) ?? [];
  return sheetColumns
    .filter((x) => !usedColumns.includes(x.value))
    .map((x) => ({ text: x.text, value: x.value.toString() }));
}

export default FieldSelectColumnList;
